import React from 'react';

type HowItWorksType = 'founder' | 'executive' | 'investor' | 'comparison';

interface HowItWorksProps {
  type: HowItWorksType;
}

interface Step {
  num: string;
  title: string;
  desc: string;
}

interface ContentItem {
  title: string;
  titleEm: string;
  subtitle: string;
  steps: Step[];
}

const HowItWorksSection: React.FC<HowItWorksProps> = ({ type }) => {
  const content: Record<HowItWorksType, ContentItem> = {
    founder: {
      title: 'From gut feeling',
      titleEm: 'to go/no-go in 72 hours.',
      subtitle: 'No consultants. No months of research. A complete simulation of your expansion built on real data, delivered in 72 hours.',
      steps: [
        { num: '01', title: 'Tell us about your expansion', desc: 'Target market, your business, team needs, and budget. Takes five minutes.' },
        { num: '02', title: 'We build your simulation', desc: 'Real local benchmarks. Your numbers. A complete 12-month model of exactly what your entry looks like — costs, revenue, risks, and break-even.' },
        { num: '03', title: 'Make the call with clarity', desc: 'Go/no-go verdict. Month-by-month cashflow. Multi-market comparison. Everything you need to decide — and defend the decision.' }
      ]
    },
    executive: {
      title: 'Three decisions it makes',
      titleEm: 'easier to get right.',
      subtitle: 'From budget sign-off to board presentations — here is how executive teams use Market Entry to move with more confidence.',
      steps: [
        { num: '01', title: 'Board presentation', desc: 'Show up with 12-month projections, full cost analysis, and market benchmarks. Numbers your board can interrogate — and trust.' },
        { num: '02', title: 'Budget validation', desc: 'The team says they need €500K. The simulation tells you if that holds up — or shows you a leaner structure that gets you there for less.' },
        { num: '03', title: 'Multi-market sequencing', desc: 'Entering 3 markets this year? Compare them side by side and build your rollout sequence based on which returns fastest and which needs more runway.' }
      ]
    },
    investor: {
      title: 'Back the right investments.',
      titleEm: 'Every time.',
      subtitle: 'From pre-investment due diligence to active portfolio management — here is how PE and VC teams put Market Entry to work.',
      steps: [
        { num: '01', title: 'Pre-investment due diligence', desc: "A SaaS company claims they'll expand to Germany and be profitable in 12 months. Run the simulation before you commit. Either the numbers hold up, or they don't." },
        { num: '02', title: 'Portfolio expansion planning', desc: 'A portfolio company wants to enter 3 new markets. Use multi-market comparison to help them sequence correctly: which market first, which to defer, and which to avoid entirely.' },
        { num: '03', title: 'Independent board-level analysis', desc: 'Walk into board meetings with a simulation you commissioned — not the one the management team built. Data-backed expansion benchmarks that hold up to scrutiny.' }
      ]
    },
    comparison: {
      title: 'One target in.',
      titleEm: 'Five markets out.',
      subtitle: 'The entire comparison is generated automatically from a single input.',
      steps: [
        { num: '01', title: 'You pick a country', desc: 'Submit your target market — Germany, Brazil, Japan, anywhere. Takes 2 minutes.' },
        { num: '02', title: 'We find alternatives', desc: 'Our system identifies 4-5 similar markets and runs a full financial simulation for each one.' },
        { num: '03', title: 'You see the ranking', desc: 'Receive a ranked comparison with Entry Scores, profitability timelines, and a clear recommendation on where to start.' }
      ]
    }
  };

  const data = content[type];

  return (
    <div className="uc-section-dark">
      <div className="uc-inner">
        <div className="uc-label"><span>How Investment Teams Use It</span></div>
        <h2 className="uc-title">{data.title}<br/><em>{data.titleEm}</em></h2>
        <p className="uc-sub">{data.subtitle}</p>
        <div className="uc-scenario">
          {data.steps.map((step, index) => (
            <div key={index} className="uc-scenario-card" style={{background:'rgba(255,255,255,0.04)', borderColor:'rgba(255,255,255,0.06)'}}>
              <div className="uc-scenario-num">Step {step.num}</div>
              <h4 style={{color:'var(--white)'}}>{step.title}</h4>
              <p style={{color:'rgba(255,255,255,0.5)'}}>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksSection;
